import { useEffect, useState } from 'react';
import type { Calendar } from '../../shared/contracts';
import { api, type ClientSession, type SecurityInfo } from '../api';
import { Loading } from '../components/Status';

export function AccountPage({
  session,
  calendars,
}: {
  session: ClientSession | null;
  calendars: Calendar[];
}) {
  const [security, setSecurity] = useState<SecurityInfo | null>(null);
  const [loading, setLoading] = useState(!session?.capabilities.local);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [passkeyName, setPasskeyName] = useState('My passkey');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [totpURI, setTotpURI] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [createdInvite, setCreatedInvite] = useState<{ id: string; url: string } | null>(null);
  const editable = calendars.filter((calendar) => calendar.canEdit);
  const [inviteCalendar, setInviteCalendar] = useState(editable[0]?.id || '');

  const loadSecurity = async () => {
    if (session?.capabilities.local) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const capabilities = await api.authCapabilities();
      setSecurity(capabilities.security ? await api.security() : null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Security settings could not load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => void loadSecurity(), [session]);

  const run = async (name: string, action: () => Promise<void>) => {
    setBusy(name);
    setError('');
    setNotice('');
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The account could not be updated');
    } finally {
      setBusy('');
    }
  };
  const addPasskey = () =>
    run('passkey', async () => {
      const { authClient } = await import('../auth-client');
      const result = await authClient.passkey.addPasskey({ name: passkeyName });
      if (result.error) throw new Error(result.error.message || 'Passkey registration failed');
      setNotice('Passkey added.');
      await loadSecurity();
    });
  const beginTotp = () =>
    run('totp-start', async () => {
      const { authClient } = await import('../auth-client');
      const result = await authClient.twoFactor.enable({
        method: 'totp',
        ...(password ? { password } : {}),
      });
      if (result.error) throw new Error(result.error.message || 'Authenticator setup failed');
      if (result.data.method !== 'totp') throw new Error('Authenticator setup was not returned');
      setTotpURI(result.data.totpURI);
      setBackupCodes(result.data.backupCodes);
      setNotice('Add this account to your authenticator, then verify one code.');
    });
  const verifyTotp = () =>
    run('totp-verify', async () => {
      const { authClient } = await import('../auth-client');
      const result = await authClient.twoFactor.verifyTotp({ code: totpCode });
      if (result.error) throw new Error(result.error.message || 'That code did not work');
      if (security?.mfaRequired) {
        window.location.assign('/account');
        return;
      }
      setTotpURI('');
      setNotice('Authenticator protection enabled. Store your backup codes safely.');
      await loadSecurity();
    });
  const verifyPasskey = () =>
    run('passkey-verify', async () => {
      const { authClient } = await import('../auth-client');
      const result = await authClient.signIn.passkey();
      if (result.error) throw new Error(result.error.message || 'Passkey verification failed');
      window.location.assign('/account');
    });
  const verifyBackupCode = () =>
    run('backup-code', async () => {
      const { authClient } = await import('../auth-client');
      const result = await authClient.twoFactor.verifyBackupCode({ code: backupCode.trim() });
      if (result.error) throw new Error(result.error.message || 'That backup code did not work');
      window.location.assign('/account');
    });
  const cancelPendingSignIn = () =>
    run('sign-out', async () => {
      const { authClient } = await import('../auth-client');
      const result = await authClient.signOut();
      if (result.error) throw new Error(result.error.message || 'Sign-out failed');
      window.location.assign('/');
    });
  const disableTotp = () =>
    run('totp-disable', async () => {
      const { authClient } = await import('../auth-client');
      const result = await authClient.twoFactor.disable(password ? { password } : {});
      if (result.error)
        throw new Error(result.error.message || 'Could not disable authenticator protection');
      setNotice('Authenticator protection disabled.');
      await loadSecurity();
    });
  const changePasskeyPolicy = (required: boolean) =>
    run('policy', async () => {
      const { authClient } = await import('../auth-client');
      const assertion = await authClient.signIn.passkey();
      if (assertion.error)
        throw new Error(assertion.error.message || 'Passkey verification failed');
      await api.updateSecurity(required);
      setNotice(required ? 'Passkey now required at sign-in.' : 'Passkey requirement removed.');
      await loadSecurity();
    });
  const invite = () =>
    run('invite', async () => {
      const result = await api.createInvitation(inviteEmail, inviteCalendar);
      setCreatedInvite({ id: result.id, url: result.url });
      setNotice('Invitation created. Share the private link below.');
      setInviteEmail('');
    });
  const revokeInvite = () =>
    run('revoke-invite', async () => {
      if (!createdInvite) return;
      await api.revokeInvitation(createdInvite.id);
      setCreatedInvite(null);
      setNotice('Invitation revoked.');
    });

  if (loading)
    return (
      <main id="main-content" className="center-page">
        <Loading label="Loading account security" />
      </main>
    );

  const identity = session?.user ?? security?.user;
  if (!identity)
    return (
      <main id="main-content" className="center-page">
        <h1>Sign in to open your account</h1>
        <a className="button" href="/login?callbackURL=%2Faccount">
          Continue to sign in
        </a>
      </main>
    );

  if (security?.mfaRequired)
    return (
      <main id="main-content" className="settings-page account-page">
        <a className="back-link" href="/">
          ← Calendar
        </a>
        <h1>Finish signing in</h1>
        <section className="settings-card security-card">
          <h2>Verify it is you</h2>
          <p>
            Complete a security check for <b>{identity.email}</b> before opening your account.
          </p>
          {error && (
            <p className="inline-error" role="alert">
              {error}
            </p>
          )}
          {security.totpEnabled && !security.requirePasskey && (
            <div className="auth-method">
              <label className="field">
                <span>Six-digit code</span>
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={totpCode}
                  onChange={(event) => setTotpCode(event.target.value)}
                />
              </label>
              <button
                className="button"
                disabled={Boolean(busy) || !totpCode}
                onClick={() => void verifyTotp()}
              >
                Verify code
              </button>
              <details>
                <summary>Use a backup code instead</summary>
                <p>Each backup code can be used once.</p>
                <label className="field">
                  <span>Backup code</span>
                  <input
                    autoComplete="off"
                    value={backupCode}
                    onChange={(event) => setBackupCode(event.target.value)}
                  />
                </label>
                <button
                  className="button button-quiet"
                  disabled={Boolean(busy) || !backupCode.trim()}
                  onClick={() => void verifyBackupCode()}
                >
                  Verify backup code
                </button>
              </details>
            </div>
          )}
          {security.passkeys.length > 0 && (
            <button
              className="button button-quiet"
              disabled={Boolean(busy)}
              onClick={() => void verifyPasskey()}
            >
              Verify with a passkey
            </button>
          )}
          <button
            className="link-button"
            disabled={Boolean(busy)}
            onClick={() => void cancelPendingSignIn()}
          >
            Cancel and sign out
          </button>
        </section>
      </main>
    );

  return (
    <main id="main-content" className="settings-page account-page">
      <a className="back-link" href="/">
        ← Calendar
      </a>
      <h1>Your account</h1>
      <section className="settings-card">
        <h2>{identity.name}</h2>
        <p>{identity.email}</p>
        {session?.capabilities.staff && (
          <div className="auth-buttons">
            <a className="button button-quiet" href="/settings">
              Calendar settings
            </a>
            {(session.user?.role === 'admin' ||
              calendars.some((calendar) => calendar.role === 'manager')) && (
              <a className="button button-quiet" href="/admin">
                Administration
              </a>
            )}
          </div>
        )}
        {!session?.capabilities.staff &&
          calendars.some((calendar) => calendar.role === 'manager') && (
            <a className="button button-quiet" href="/admin">
              Administration
            </a>
          )}
      </section>
      {error && (
        <p className="inline-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="inline-success" role="status">
          {notice}
        </p>
      )}
      {(security || session?.capabilities.local || session?.capabilities.parentInvites) && (
        <>
          {loading && <Loading label="Loading account security" />}
          {session?.capabilities.local && (
            <section className="settings-card">
              <h2>Local fixture account</h2>
              <p>
                Passkey and authenticator setup require a real Better Auth session. Protocol
                coverage runs separately against workerd.
              </p>
            </section>
          )}
          {security && (
            <section className="settings-card security-card">
              <h2>Sign-in security</h2>
              <h3>Passkeys</h3>
              {security.passkeys.length ? (
                <ul>
                  {security.passkeys.map((passkey) => (
                    <li key={passkey.id}>
                      {passkey.name}{' '}
                      <button
                        className="link-button"
                        disabled={
                          Boolean(busy) ||
                          (security.requirePasskey && security.passkeys.length === 1)
                        }
                        onClick={() =>
                          void run('delete-passkey', async () => {
                            const { authClient } = await import('../auth-client');
                            const result = await authClient.passkey.deletePasskey({
                              id: passkey.id,
                            });
                            if (result.error)
                              throw new Error(result.error.message || 'Could not remove passkey');
                            setNotice('Passkey removed.');
                            await loadSecurity();
                          })
                        }
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No passkeys registered.</p>
              )}
              {security.requirePasskey && security.passkeys.length === 1 && (
                <p>
                  Add another passkey or turn off the passkey requirement before removing your last
                  one.
                </p>
              )}
              <div className="inline-form">
                <label className="field">
                  <span>Passkey name</span>
                  <input
                    value={passkeyName}
                    onChange={(event) => setPasskeyName(event.target.value)}
                  />
                </label>
                <button
                  className="button"
                  disabled={Boolean(busy) || !passkeyName}
                  onClick={() => void addPasskey()}
                >
                  Add passkey
                </button>
              </div>
              {security.passkeys.length > 0 && (
                <label className="policy-control">
                  <input
                    type="checkbox"
                    checked={security.requirePasskey}
                    disabled={Boolean(busy)}
                    onChange={(event) => void changePasskeyPolicy(event.target.checked)}
                  />
                  Require a passkey each time I sign in
                </label>
              )}
              <h3>Authenticator app</h3>
              {security.totpEnabled ? (
                <div className="inline-form">
                  <p>Authenticator protection is enabled.</p>
                  <label className="field">
                    <span>Password, if this account uses one</span>
                    <input
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  </label>
                  <button
                    className="button button-quiet"
                    disabled={Boolean(busy)}
                    onClick={() => void disableTotp()}
                  >
                    Disable authenticator
                  </button>
                </div>
              ) : (
                <div className="inline-form">
                  <label className="field">
                    <span>Password, if this account uses one</span>
                    <input
                      type="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                    />
                  </label>
                  <button
                    className="button button-quiet"
                    disabled={Boolean(busy)}
                    onClick={() => void beginTotp()}
                  >
                    Set up authenticator
                  </button>
                </div>
              )}
              {totpURI && (
                <div className="totp-setup">
                  <p>Open this setup link on a device with your authenticator app:</p>
                  <a href={totpURI}>Add to authenticator</a>
                  <label className="field">
                    <span>Authenticator code</span>
                    <input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={totpCode}
                      onChange={(event) => setTotpCode(event.target.value)}
                    />
                  </label>
                  <button
                    className="button"
                    disabled={Boolean(busy) || !totpCode}
                    onClick={() => void verifyTotp()}
                  >
                    Verify and enable
                  </button>
                </div>
              )}
              {backupCodes.length > 0 && (
                <div className="backup-codes">
                  <b>Backup codes</b>
                  <ul>
                    {backupCodes.map((code) => (
                      <li key={code}>
                        <code>{code}</code>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}
          {session?.capabilities.parentInvites &&
            session.capabilities.staff &&
            editable.length > 0 && (
              <section className="settings-card">
                <h2>Invite a family member</h2>
                <p>The recipient must sign in with this exact email address.</p>
                <div className="inline-form">
                  <label className="field">
                    <span>Email address</span>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(event) => setInviteEmail(event.target.value)}
                    />
                  </label>
                  <label className="field">
                    <span>Calendar</span>
                    <select
                      value={inviteCalendar}
                      onChange={(event) => setInviteCalendar(event.target.value)}
                    >
                      {editable.map((calendar) => (
                        <option key={calendar.id} value={calendar.id}>
                          {calendar.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    className="button"
                    disabled={Boolean(busy) || !inviteEmail || !inviteCalendar}
                    onClick={() => void invite()}
                  >
                    Create invitation
                  </button>
                </div>
                {createdInvite && (
                  <div className="invite-result">
                    <label className="field">
                      <span>Private invitation link</span>
                      <input
                        readOnly
                        value={createdInvite.url}
                        onFocus={(event) => event.currentTarget.select()}
                      />
                    </label>
                    <button
                      className="link-button"
                      disabled={Boolean(busy)}
                      onClick={() => void revokeInvite()}
                    >
                      Revoke this invitation
                    </button>
                  </div>
                )}
              </section>
            )}
        </>
      )}
    </main>
  );
}
